import { useEffect, useMemo, useState } from 'react';
import { CalendarEvent } from '../../types/calendar';
import { isToday, isSameDay, addDays, getEventCardStyle } from '../../utils/calendar';
import { formatTime, formatDate } from '../../utils/time';
import WeatherWidget from '../WeatherWidget';
import { useTodoStore } from '../../store/todoStore';
import { Todo } from '../../types/todo';
import { Icon } from '../../utils/icons';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import MemberAvatar from '../MemberAvatar';
import { useFamilyStore } from '../../store/familyStore';
import LoadingSpinner from '../LoadingSpinner';
import { useToastStore } from '../../store/toastStore';

interface MobileDayViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
  weatherPosition?: 'top' | 'bottom';
}

export default function MobileDayView({
  date,
  events,
  onDateClick,
  onEventClick,
  weatherPosition = 'top',
}: MobileDayViewProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const isDateToday = isToday(date);
  const { todos, fetchTodos, updateTodo } = useTodoStore();
  const { family } = useFamilyStore();
  const familyMembers = family?.members || [];
  const { showToast } = useToastStore();
  const [pendingStatusIds, setPendingStatusIds] = useState<Record<string, boolean>>({});

  // Fetch todos when date changes
  useEffect(() => {
    fetchTodos().catch((error) => {
      console.error('Error fetching todos:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
    });
  }, [date, fetchTodos, showToast]);

  // Filter events for this date
  // Get view date string in local timezone (YYYY-MM-DD)
  const viewDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

  // Filter todos for this day (always include past due todos)
  const dayTodos = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return todos.filter(todo => {
      // Only show open todos (not done)
      if (todo.status === 'done') return false;
      
      if (!todo.dueDateTime) return false; // Only show todos with due dates
      // Parse dueDateTime, handling both Date objects and ISO strings
      // If it's an ISO string, extract date components directly to avoid timezone shifts
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        // Check if it's a date-only string (YYYY-MM-DD) or full ISO string
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Date-only string: parse as local date
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          // Full ISO string: extract date components from UTC to avoid timezone shifts
          const isoDate = new Date(dueStr);
          // Use UTC methods to get the date components, then create local date
          const year = isoDate.getUTCFullYear();
          const month = isoDate.getUTCMonth();
          const day = isoDate.getUTCDate();
          dueDate = new Date(year, month, day);
        }
      } else {
        dueDate = new Date();
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      
      // Always show past due todos, and also show todos due on the selected date
      const isPastDue = dueDateStart.getTime() < today.getTime();
      const isDueOnSelectedDate = dueDateStart.getTime() >= dayStart.getTime() && dueDateStart.getTime() <= dayEnd.getTime();
      
      return isPastDue || isDueOnSelectedDate;
    }).sort((a, b) => {
      // First, separate past due from todos due on selected date (past due first)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const aDueDate = a.dueDateTime instanceof Date ? a.dueDateTime : (a.dueDateTime ? new Date(a.dueDateTime) : new Date());
      const aDueDateStart = new Date(aDueDate.getFullYear(), aDueDate.getMonth(), aDueDate.getDate(), 0, 0, 0);
      const aIsPastDue = aDueDateStart.getTime() < today.getTime();
      
      const bDueDate = b.dueDateTime instanceof Date ? b.dueDateTime : (b.dueDateTime ? new Date(b.dueDateTime) : new Date());
      const bDueDateStart = new Date(bDueDate.getFullYear(), bDueDate.getMonth(), bDueDate.getDate(), 0, 0, 0);
      const bIsPastDue = bDueDateStart.getTime() < today.getTime();
      
      if (aIsPastDue !== bIsPastDue) {
        return aIsPastDue ? -1 : 1; // Past due first
      }
      
      // Sort by status (open first), then priority, then due time
      if (a.status !== b.status) {
        return a.status === 'open' ? -1 : 1;
      }
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.priority || 'medium'];
      const bPriority = priorityOrder[b.priority || 'medium'];
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      if (a.dueDateTime && b.dueDateTime) {
        const aTime = a.dueDateTime instanceof Date ? a.dueDateTime : (a.dueDateTime ? new Date(a.dueDateTime) : new Date());
        const bTime = b.dueDateTime instanceof Date ? b.dueDateTime : (b.dueDateTime ? new Date(b.dueDateTime) : new Date());
        return aTime.getTime() - bTime.getTime();
      }
      return 0;
    });
  }, [todos, dayStart, dayEnd]);
  
  const dayEvents = events
    .filter(event => {
      // Events are already Date objects from the store conversion
      const eventStart = event.startTime instanceof Date 
        ? new Date(event.startTime) 
        : new Date(event.startTime);
      const eventEnd = event.endTime instanceof Date 
        ? new Date(event.endTime) 
        : new Date(event.endTime);
      
      // Get event date strings in local timezone (YYYY-MM-DD)
      const eventStartStr = `${eventStart.getFullYear()}-${String(eventStart.getMonth() + 1).padStart(2, '0')}-${String(eventStart.getDate()).padStart(2, '0')}`;
      const eventEndStr = `${eventEnd.getFullYear()}-${String(eventEnd.getMonth() + 1).padStart(2, '0')}-${String(eventEnd.getDate()).padStart(2, '0')}`;
      
      // Event overlaps if: eventStart date <= viewDate AND eventEnd date >= viewDate
      const overlaps = eventStartStr <= viewDateStr && eventEndStr >= viewDateStr;
      
      return overlaps;
    })
    .sort((a, b) => {
      const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
      const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
      return aStart.getTime() - bStart.getTime();
    });

  // Generate time slots (every hour from 6 AM to 11 PM)
  const timeSlots = [];
  for (let hour = 6; hour <= 23; hour++) {
    timeSlots.push(hour);
  }

  const getEventsForHour = (hour: number): CalendarEvent[] => {
    return dayEvents.filter(event => {
      const eventStart = new Date(event.startTime);
      const eventHour = eventStart.getHours();
      return eventHour === hour || (eventHour < hour && new Date(event.endTime).getHours() > hour);
    });
  };

  const handleTodoToggle = async (todoId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'open' ? 'done' : 'open';
    setPendingStatusIds((state) => ({ ...state, [todoId]: true }));
    updateTodo(todoId, { status: newStatus })
      .then(() => {
        showToast(newStatus === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
      })
      .catch((error) => {
        console.error('Error updating todo status:', error);
        showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
      })
      .finally(() => {
        setPendingStatusIds((state) => {
          const next = { ...state };
          delete next[todoId];
          return next;
        });
      });
  };

  const renderTodo = (todo: Todo) => {
    // Check if todo is past due
    const isPastDue = (): boolean => {
      if (!todo.dueDateTime || todo.status === 'done') return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Parse dueDateTime, handling both Date objects and ISO strings
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          const isoDate = new Date(dueStr);
          dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
        }
      } else {
        dueDate = new Date();
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      return dueDateStart.getTime() < today.getTime();
    };

    const pastDue = isPastDue();
    const priorityColors = {
      critical: 'bg-red-50 border-red-300',
      high: 'bg-orange-50 border-orange-300',
      medium: 'bg-blue-50 border-blue-300',
      low: 'bg-gray-50 border-gray-300',
    };
    const priorityColor = priorityColors[todo.priority || 'medium'];
    const isDone = todo.status === 'done';
    
    // Override colors for past due todos
    const pastDueColors = pastDue 
      ? 'bg-red-50 border-red-400'
      : priorityColor;

    return (
      <div
        key={todo.todoId}
        className={`border rounded-landing p-3 flex items-start gap-2 cursor-pointer touch-manipulation active:scale-[0.98] transition-all ${pastDueColors} ${isDone ? 'opacity-60' : ''} min-h-[60px]`}
        onClick={() => {
          if (!pendingStatusIds[todo.todoId]) {
            handleTodoToggle(todo.todoId, todo.status);
          }
        }}
      >
        <button
          className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 min-h-[24px] min-w-[24px] ${
            isDone ? 'bg-green-500 border-green-600' : 'bg-white border-gray-300'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleTodoToggle(todo.todoId, todo.status);
          }}
          aria-label="Toggle status"
          disabled={pendingStatusIds[todo.todoId]}
        >
          {pendingStatusIds[todo.todoId] ? (
            <LoadingSpinner size="sm" label="Updating todo" />
          ) : (
            isDone && <Icon name="check" size="sm" className="text-white" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-gray-900 text-sm font-semibold ${isDone ? 'line-through' : ''}`}>
            {todo.title}
          </div>
          {todo.dueDateTime && (
            <div className={`${pastDue ? 'text-red-600' : 'text-gray-500'} text-xs mt-0.5 font-semibold`}>
              {pastDue ? 'Past due' : formatTime(todo.dueDateTime instanceof Date ? todo.dueDateTime : (todo.dueDateTime ? new Date(todo.dueDateTime) : new Date()))}
            </div>
          )}
          {todo.assigneeName && (() => {
            const assigneeMember = todo.assigneeId 
              ? familyMembers.find(m => m.userId === todo.assigneeId)
              : null;
            const assigneeColor = assigneeMember?.color || '#3b82f6';
            const memberForAvatar = assigneeMember
              ? {
                  memberId: assigneeMember.userId,
                  memberName: assigneeMember.name,
                  memberColor: assigneeMember.color,
                  memberAvatar: assigneeMember.avatar,
                }
              : {
                  memberId: todo.assigneeId,
                  memberName: todo.assigneeName,
                  memberColor: assigneeColor,
                  memberAvatar: undefined,
                };
            return (
              <div className="flex items-center gap-1.5 mt-0.5">
                <MemberAvatar
                  member={memberForAvatar}
                  size="sm"
                />
                <span className="text-xs text-gray-600">{memberForAvatar.memberName}</span>
              </div>
            );
          })()}
        </div>
        {todo.priority && todo.priority !== 'medium' && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
            todo.priority === 'critical' ? 'bg-red-500 text-white' :
            todo.priority === 'high' ? 'bg-orange-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {todo.priority}
          </span>
        )}
      </div>
    );
  };

  // Weather component - very compact on mobile
  const weatherComponent = (
    <div className="px-2 py-1 sm:px-3 sm:py-2 border-b border-gray-200 flex-shrink-0 relative z-0">
      <WeatherWidget 
        isDarkMode={false}
        location="device"
        variant="compact"
      />
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        {/* Date Header - z-20 ensures it stays above scrolling event/todo content */}
        <div className="sticky top-0 bg-white border-b border-gray-200 z-20 px-4 py-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isDateToday ? 'Today' : formatDate(date, 'long').split(',')[0]}
              </h2>
              <p className="text-sm text-gray-600">
                {formatDate(date, 'long')}
              </p>
            </div>
            {onDateClick && (
              <button
                onClick={() => onDateClick(date)}
                className={`px-4 py-2 ${tokens.button.primary} font-semibold rounded-landing min-h-[44px]`}
              >
                + Add
              </button>
            )}
          </div>
        </div>

        {/* Weather at top (if position is 'top') */}
        {weatherPosition === 'top' && weatherComponent}

        {/* Events and Todos Section */}
        <div className="px-3 py-2">
        {dayEvents.length === 0 && dayTodos.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p className="text-base">No events or todos scheduled</p>
            {onDateClick && (
              <button
                onClick={() => onDateClick(date)}
                className={`mt-4 px-6 py-3 ${tokens.button.primary} font-semibold rounded-landing min-h-[48px]`}
              >
                Create Event
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Events */}
            {dayEvents.length > 0 && (
              <div className="space-y-2">
                {dayEvents.map((event) => {
              const startTime = new Date(event.startTime);
              const endTime = new Date(event.endTime);
              const isAllDay = event.isAllDay === true || event.isImportantDate || 
                (startTime.getHours() === 0 && startTime.getMinutes() === 0 && 
                 endTime.getHours() === 23 && endTime.getMinutes() === 59);

              // Support both legacy (single member) and new (multiple members) format
              const members = event.members && event.members.length > 0
                ? event.members.map(m => ({
                    ...m,
                    memberColor: m.memberColor || '#3b82f6', // Ensure memberColor is always set
                  }))
                : (event.memberId ? [{
                    memberId: event.memberId,
                    memberName: event.memberName || '',
                    memberColor: event.memberColor || '#3b82f6',
                    memberAvatar: event.memberAvatar,
                  }] : []);

              const cardStyle = getEventCardStyle(members, false);
              const hasLeftBar = cardStyle.leftBarGradient;
              const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
              const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 12}px` : undefined;

              return (
                <div
                  key={event.id}
                  onClick={() => onEventClick?.(event)}
                  className="rounded-landing p-3 shadow-sm border border-gray-200 active:bg-gray-50 min-h-[80px] relative"
                  style={{
                    background: cardStyle.background || 'rgba(255, 255, 255, 0.8)',
                    paddingLeft: paddingLeft,
                  }}
                >
                  {/* Left bar gradient overlay for multi-member events */}
                  {hasLeftBar && (
                    <div
                      className="absolute left-0 top-0 bottom-0"
                      style={{
                        width: cardStyle.leftBarWidth || '4px',
                        background: cardStyle.leftBarGradient,
                        borderTopLeftRadius: '18px', // Match rounded-landing (18px)
                        borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
                        zIndex: 1,
                      }}
                    />
                  )}
                  <div className="flex items-start justify-between gap-3 relative z-10">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {isAllDay ? 'All Day' : formatTime(startTime)}
                        </span>
                        {!isAllDay && (
                          <span className="text-xs text-gray-500">
                            - {formatTime(endTime)}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        {event.title}
                      </h3>
                      {members && members.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {members.slice(0, 2).map((member, idx) => (
                            <div key={member.memberId || idx} className="flex items-center gap-1">
                              <MemberAvatar
                                member={member}
                                size="sm"
                              />
                              <span className="text-xs text-gray-600">{member.memberName}</span>
                            </div>
                          ))}
                          {members.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{members.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      {event.location && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-xs">📍</span>
                          <span className="text-xs text-gray-600 truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
                })}
              </div>
            )}

            {/* Todos Section */}
            {dayTodos.length > 0 && (
              <div className={dayEvents.length > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                <h3 className="text-base font-semibold text-gray-900 mb-3">To Dos</h3>
                <div className="space-y-2">
                  {dayTodos.map(renderTodo)}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Weather at bottom (if position is 'bottom') */}
        {weatherPosition === 'bottom' && weatherComponent}
      </div>
    </div>
  );
}
